/**
 * ImportExportButtons — admin/owner-gated Import + Export buttons for the
 * 11E.3 page-toolbar (`PageContainer`'s `actions` slot), shared by all five
 * 11E.9 entities. Drop into any entity list page next to its "+ Add" button.
 *
 * Export honours the caller's current filtered view (status/search) or a
 * bulk-selected id list — whichever the page passes in.
 */

import React, { useState } from 'react';
import { Button } from '@/components/common/Button';
import { useAuth } from '@/context';
import { exportEntityCsv } from '../api/imports';
import { ImportDialog } from './ImportDialog';

interface Props {
  entity: string;
  entityLabel: string;
  /** Currently bulk-selected row ids (EntityTable checkbox column), if any. */
  selectedIds?: string[];
  /** Current filter/search state so "export current view" matches the table. */
  exportStatus?: string;
  exportSearch?: string;
}

export const ImportExportButtons: React.FC<Props> = ({
  entity,
  entityLabel,
  selectedIds,
  exportStatus,
  exportSearch,
}) => {
  const { hasRole } = useAuth();
  const [importOpen, setImportOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  if (!hasRole('admin', 'owner')) return null;

  const handleExport = async () => {
    setExporting(true);
    try {
      await exportEntityCsv(entity, {
        ids: selectedIds && selectedIds.length > 0 ? selectedIds : undefined,
        status: !selectedIds || selectedIds.length === 0 ? exportStatus : undefined,
        search: !selectedIds || selectedIds.length === 0 ? exportSearch : undefined,
      });
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        onClick={() => setImportOpen(true)}
        data-testid={`candeza-${entity}-import-btn`}
      >
        Import
      </Button>
      <Button
        variant="outline"
        onClick={handleExport}
        disabled={exporting}
        data-testid={`candeza-${entity}-export-btn`}
      >
        {exporting ? 'Exporting…' : selectedIds && selectedIds.length > 0 ? `Export (${selectedIds.length})` : 'Export'}
      </Button>
      <ImportDialog
        isOpen={importOpen}
        onClose={() => setImportOpen(false)}
        entity={entity}
        entityLabel={entityLabel}
      />
    </>
  );
};
